/**
 * Composant NotificationPanel - Panneau dropdown de notifications
 * Affiché depuis le TopNav/ProductHeader avec uniquement des notifications
 */

import React from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  CreditCard,
  TrendingUp,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import { cn } from "@core/lib/utils";
import { CreditInfo } from "@core/services/CreditCalculationService";
import { NotificationItem } from "@core/types/notifications";

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  creditInfo: CreditInfo;
  notifications: NotificationItem[];
  encouragementMessage: string;
  className?: string;
  onDismiss?: (id: string) => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
  creditInfo,
  notifications,
  encouragementMessage,
  className = "",
  onDismiss,
}) => {
  const { t, i18n } = useTranslation("common");
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panneau */}
      <div
        className={cn(
          "absolute top-16 right-4 w-80 max-h-[400px] bg-background border border-border rounded-lg shadow-2xl overflow-hidden",
          "md:top-20 md:right-8",
          "animate-in slide-in-from-top-2 fade-in-0 duration-200",
          className,
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {t("notifications_title")}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-accent transition-colors"
            aria-label={t("close")}
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto max-h-[300px]">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm">{t("notifications_empty")}</p>
            </div>
          ) : (
            <div className="space-y-1">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "p-3 border-b border-border last:border-b-0",
                    !notification.read &&
                      "bg-primary/5 border-l-2 border-l-primary",
                  )}
                >
                  <div className="flex items-start gap-2">
                    <div
                      className={cn(
                        "w-2 h-2 rounded-full mt-2 flex-shrink-0",
                        notification.priority === "critique"
                          ? "bg-destructive"
                          : notification.priority === "élevée"
                            ? "bg-warning"
                            : notification.priority === "moyenne"
                              ? "bg-primary"
                              : "bg-muted-foreground",
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="text-sm font-medium text-foreground">
                          {notification.title}
                        </h4>
                        {onDismiss && notification.dismissible && (
                          <button
                            onClick={() => onDismiss(notification.id)}
                            className="p-0.5 -m-0.5 rounded hover:bg-accent transition-colors flex-shrink-0"
                            aria-label="Marquer comme lue"
                            title="Marquer comme lue"
                          >
                            <X className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        {notification.message}
                      </p>
                      {notification.callToAction && (
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => {
                              // Fermer le panneau
                              onClose();
                              // Naviguer vers l'URL
                              window.location.href =
                                notification.callToAction!.url;
                            }}
                            className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 font-medium transition-colors"
                          >
                            {notification.callToAction.label}
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      {notification.secondaryCallToAction && (
                        <div className="flex items-center gap-2 mt-1">
                          <button
                            onClick={() => {
                              // Fermer le panneau
                              onClose();
                              // Naviguer vers l'URL
                              window.location.href =
                                notification.secondaryCallToAction!.url;
                            }}
                            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 font-medium transition-colors"
                          >
                            {notification.secondaryCallToAction.label}
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(notification.timestamp).toLocaleString(
                          i18n.language || "fr",
                          {
                            dateStyle: "short",
                            timeStyle: "short",
                          },
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPanel;
